import { useCallback, useEffect, useId, useRef, useState, type RefObject } from "react"
import { Check } from "@untitledui/icons"
import { cx } from "@/lib/utils/cx"
import { isVinValid } from "@/lib/vin/vin-validator"
import { carName, VIN_COPY as C, type VinStep } from "./use-vin-step"
import { typedVin } from "./vin-text"

// The VIN step's answers, as a card: reading / looking up, "Is it a …?",
// a VIN we couldn't look up, and typing it. Dark over the live camera,
// light on the phone-camera screen. Nothing to show while it's just looking.
// Each card takes the focus when it appears (its main control); the host
// announces it (vinAnnouncement) from one live region that's always there.

// Focus the element when it mounts (each card state is its own element).
function useFocusOnMount() {
  return useCallback((el: HTMLElement | null) => el?.focus({ preventScroll: true }), [])
}

// When the card goes (cancel, "Not quite", a missed read) the focused
// control goes with it: hand the focus to the step's first control instead
// of letting it fall to the page.
export function useFocusAfterCard(cardUp: boolean, onStep: boolean, controls: RefObject<HTMLElement | null>) {
  const was = useRef(cardUp)
  useEffect(() => {
    const closed = was.current && !cardUp
    was.current = cardUp
    if (!closed || !onStep) return
    const active = document.activeElement
    if (active && active !== document.body) return
    controls.current?.querySelector<HTMLElement>("button:not([disabled])")?.focus({ preventScroll: true })
  }, [cardUp, onStep, controls])
}

export function VinCard({ step, tone }: { step: VinStep; tone: "dark" | "light" }) {
  const s = step.state
  const focusOnMount = useFocusOnMount()
  const titleId = useId()
  if (s.kind === "idle") return null
  const dark = tone === "dark"
  const shell = cx(
    "rounded-3xl p-4",
    dark ? "bg-zinc-950/92 text-white shadow-2xl ring-1 ring-white/10 backdrop-blur-xl" : "bg-white text-ink ring-1 ring-ink/10",
  )
  const primary = cx(
    "flex h-12 flex-1 items-center justify-center gap-2 rounded-2xl text-[15px] font-semibold focus-visible:outline-2 focus-visible:outline-offset-2",
    dark ? "bg-white text-ink hover:bg-zinc-100 focus-visible:outline-white" : "bg-ink text-white hover:bg-black focus-visible:outline-ink",
  )
  const secondary = cx(
    "flex h-12 flex-1 items-center justify-center rounded-2xl text-[15px] font-semibold focus-visible:outline-2",
    dark
      ? "border border-white/25 text-white hover:bg-white/10 focus-visible:outline-white"
      : "bg-white text-ink ring-1 ring-ink/15 hover:ring-ink/30 focus-visible:outline-ink",
  )
  const muted = dark ? "text-white/70" : "text-zinc-600"

  if (s.kind === "reading" || s.kind === "looking-up") {
    return (
      <div
        key={s.kind}
        className={cx(shell, "flex items-center gap-3")}
        role="group"
        aria-labelledby={titleId}
        data-testid="vin-card"
        data-vin-state={s.kind}
      >
        <span
          className={cx(
            "size-5 shrink-0 animate-spin rounded-full border-2",
            dark ? "border-white/25 border-t-white" : "border-ink/15 border-t-ink",
          )}
          aria-hidden
        />
        <p id={titleId} className="flex-1 text-[16px] font-medium">
          {s.kind === "reading" ? C.reading : C.lookingUp}
        </p>
        {/* A stalled connection never traps them: back to the barcode, a photo, or typing. */}
        <button ref={focusOnMount} type="button" onClick={step.cancel} className={cx(secondary, "h-11 flex-none px-4")}>
          {C.cancel}
        </button>
      </div>
    )
  }

  if (s.kind === "confirm") {
    return (
      <div className={shell} role="group" aria-labelledby={titleId} data-testid="vin-card" data-vin-state="confirm">
        <p id={titleId} className="text-[18px] font-semibold leading-snug">
          {C.confirm(carName(s.found))}
        </p>
        <p className={cx("mt-1 font-mono text-[13px] tracking-wide", muted)}>{C.vinLabel(s.vin)}</p>
        <div className="mt-4 flex gap-2">
          <button type="button" onClick={step.reject} className={secondary}>
            {C.no}
          </button>
          <button ref={focusOnMount} type="button" onClick={step.confirm} className={primary}>
            <Check className="size-4" strokeWidth={3} aria-hidden />
            {C.yes}
          </button>
        </div>
      </div>
    )
  }

  if (s.kind === "unknown") {
    return (
      <div className={shell} role="group" aria-labelledby={titleId} data-testid="vin-card" data-vin-state="unknown">
        <p id={titleId} className="text-[18px] font-semibold leading-snug">
          {C.unknownTitle}
        </p>
        <p className={cx("mt-1 text-[15px] leading-snug", muted)}>{C.unknownBody}</p>
        <p className={cx("mt-1 font-mono text-[13px] tracking-wide", muted)}>{C.vinLabel(s.vin)}</p>
        <div className="mt-4 flex gap-2">
          <button type="button" onClick={step.type} className={secondary}>
            {C.type}
          </button>
          <button ref={focusOnMount} type="button" onClick={step.confirm} className={primary}>
            {C.continue}
          </button>
        </div>
      </div>
    )
  }

  return <TypeVin step={step} tone={tone} shell={shell} primary={primary} secondary={secondary} muted={muted} />
}

function TypeVin({
  step,
  tone,
  shell,
  primary,
  secondary,
  muted,
}: {
  step: VinStep
  tone: "dark" | "light"
  shell: string
  primary: string
  secondary: string
  muted: string
}) {
  const [value, setValue] = useState("")
  const id = useId()
  const error = step.state.kind === "typing" ? step.state.error : null
  const complete = value.length === 17
  return (
    <form
      className={shell}
      data-testid="vin-card"
      data-vin-state="typing"
      onSubmit={(e) => {
        e.preventDefault()
        if (complete) step.typed(value, isVinValid(value))
      }}
    >
      <label htmlFor={id} className="text-[18px] font-semibold">
        {C.typeTitle}
      </label>
      <p id={`${id}-hint`} className={cx("mt-1 text-[14px] leading-snug", muted)}>
        {C.typeHint}
      </p>
      <input
        id={id}
        value={value}
        onChange={(e) => setValue(typedVin(e.target.value))}
        autoFocus
        autoCapitalize="characters"
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        inputMode="text"
        maxLength={20}
        aria-describedby={`${id}-hint${error ? ` ${id}-error` : ""}`}
        aria-invalid={!!error || undefined}
        placeholder="1HGCV1F34MA000000"
        className={cx(
          "mt-3 h-13 w-full rounded-xl px-4 font-mono text-[18px] tracking-[0.12em] outline-none",
          tone === "dark"
            ? "bg-white/10 text-white ring-1 ring-white/20 placeholder:text-white/30 focus:ring-2 focus:ring-white"
            : "bg-white text-ink ring-1 ring-ink/20 placeholder:text-zinc-400 focus:ring-2 focus:ring-ink",
        )}
      />
      <p className={cx("mt-1.5 text-[13px]", muted)}>{value.length} of 17</p>
      {error && (
        <p id={`${id}-error`} role="alert" className={cx("mt-1 text-[14px] font-medium", tone === "dark" ? "text-amber-300" : "text-amber-800")}>
          {error}
        </p>
      )}
      <div className="mt-3 flex gap-2">
        <button type="button" onClick={step.cancel} className={secondary}>
          {C.cancel}
        </button>
        <button type="submit" disabled={!complete} className={cx(primary, "disabled:opacity-50")}>
          {C.continue}
        </button>
      </div>
    </form>
  )
}
